/**
 * <sticky-atc-panel> — sticky købsbar på produktsiden.
 *
 * Hvorfor den var usynlig:
 *  1) Temaets markup giver panelet utility-klassen 'invisible'
 *     (main.css: visibility:hidden) og ingen fjernede den igen. Baren var
 *     derfor skjult på ALLE skærmstørrelser. Styres nu i setOut().
 *  2) Den gamle logik læste boundingClientRect fra IntersectionObserver-
 *     snapshottet, som kunne lande på den forkerte side af nul uden at der
 *     kom et nyt callback. Nu bruges observer + scroll kun som triggere, og
 *     tilstanden regnes altid ud fra live-geometri i update().
 *
 * Leveringsstriben: panelets markup ligger i main-product.liquid (112 KB),
 * så striben bygges her i JS. Cutoff, tekst og farver læses fra produktsidens
 * egen timer-blok (.st-wrap) — én kilde til sandheden, så striben arver dage,
 * lagerregler og 'Leveringslovning'-knappen. Er timeren skjult, bygges
 * striben ikke.
 *
 * Widgets: chat (Wilmo), Hello Retail og PushOwl ligger nederst til venstre og
 * låser deres placering med !important. Løftet sættes derfor inline fra JS.
 */
if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.cc-main-product');
      if (!this.section) return;

      this.anchor = this.section.querySelector('.lh-buy-row')
        || this.section.querySelector('.product-info__add-to-cart')
        || this.section.querySelector('product-form');
      if (!this.anchor) return;

      this.mainAddButton = this.section.querySelector('.js-product-form-main button[name="add"]')
        || this.section.querySelector('button[name="add"]');
      this.panelButton = this.querySelector('.card__buttons a, .card__buttons button');

      this.buildStrip();
      this.buildSaveBadge();

      this.onScroll = this.requestUpdate.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('on:variant:change', this.onScroll);
      document.addEventListener('visibilitychange', this.onScroll);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onScroll, {
          threshold: [0, 0.01, 1],
          rootMargin: '200px 0px 200px 0px'
        });
        this.observer.observe(this.anchor);
        const footer = document.querySelector('.cc-footer, footer, #footer');
        if (footer) this.observer.observe(footer);
      }

      if (this.panelButton) {
        this.onButtonClick = this.handleButtonClick.bind(this);
        this.panelButton.addEventListener('click', this.onButtonClick);
      }

      this.setOut(true);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('on:variant:change', this.onScroll);
      document.removeEventListener('visibilitychange', this.onScroll);
      if (this.observer) this.observer.disconnect();
      if (this.panelButton && this.onButtonClick) {
        this.panelButton.removeEventListener('click', this.onButtonClick);
      }
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.clock) window.clearInterval(this.clock);
      this.liftWidgets(false);
      document.body.classList.remove('lh-satc-on');
    }

    /* ---------- Leveringsstribe ---------- */

    /**
     * Bygger striben ud fra produktsidens timer-blok. Findes den ikke, eller
     * er den skjult (forkert dag/tid, udsolgt, lovning slået fra), gør vi intet.
     */
    buildStrip() {
      const src = this.section.querySelector('.st-wrap');
      if (!src || src.offsetParent === null) return;

      const h = parseInt(src.dataset.cutoffH, 10);
      const m = parseInt(src.dataset.cutoffM, 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return;
      this.cutoffHour = h;
      this.cutoffMinute = m;

      const headline = (src.querySelector('.st-headline') || {}).textContent || 'Levering i morgen';
      const css = getComputedStyle(src);

      const strip = document.createElement('div');
      strip.className = 'satc-strip';
      strip.style.setProperty('--satc-strip-bg', css.getPropertyValue('--st-bg').trim() || '#f0fdf4');
      strip.style.setProperty('--satc-strip-text', css.getPropertyValue('--st-text').trim() || '#14532d');
      strip.style.setProperty('--satc-strip-accent', css.getPropertyValue('--st-accent').trim() || '#16a34a');

      const dot = document.createElement('span');
      dot.className = 'satc-strip__dot';
      dot.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'satc-strip__label';
      label.textContent = headline.trim().replace(/!$/, '');

      this.clockEl = document.createElement('span');
      this.clockEl.className = 'satc-strip__clock';
      this.clockEl.textContent = '--:--:--';

      strip.append(dot, label, this.clockEl);
      this.prepend(strip);
      this.strip = strip;

      this.tickStrip();
      this.clock = window.setInterval(this.tickStrip.bind(this), 1000);
    }

    /**
     * Opdaterer nedtællingen. Fjerner striben når cutoff er passeret.
     */
    tickStrip() {
      if (!this.strip) return;
      const target = new Date();
      target.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);
      const diff = target - new Date();

      if (diff <= 0) {
        window.clearInterval(this.clock);
        this.strip.remove();
        this.strip = null;
        this.lastHeight = null; // tvinger nyt mål + nyt widget-løft
        this.requestUpdate();
        return;
      }

      const pad = (n) => String(n).padStart(2, '0');
      this.clockEl.textContent = `${pad(Math.floor(diff / 3600000))}:${pad(Math.floor((diff % 3600000) / 60000))}:${pad(Math.floor((diff % 60000) / 1000))}`;
    }

    /* ---------- Besparelses-badge ---------- */

    /**
     * Dansk prisformat: punktum er tusindskiller, komma er decimal.
     * @param {string} text - Pristeksten som den står på siden.
     * @returns {number}
     */
    static parsePrice(text) {
      const match = (text || '').replace(/\s/g, '').match(/[\d.,]+/);
      if (!match) return NaN;
      return parseFloat(match[0].replace(/\./g, '').replace(',', '.'));
    }

    /**
     * Sætter "SPAR x%" ind når begge priser kunne læses og rabatten er reel.
     */
    buildSaveBadge() {
      const info = this.querySelector('.card__info');
      const price = this.querySelector('.price');
      if (!info || !price || !price.classList.contains('price--on-sale')) return;

      const wasEl = price.querySelector('.price__was');
      if (!wasEl) return;

      const was = StickyAtcPanel.parsePrice(wasEl.textContent);
      const nowText = (price.textContent || '').replace(wasEl.textContent || '', '');
      const now = StickyAtcPanel.parsePrice(nowText);
      if (!Number.isFinite(was) || !Number.isFinite(now) || was <= 0 || now >= was) return;

      const pct = Math.round(((was - now) / was) * 100);
      if (pct < 5) return;

      const badge = document.createElement('span');
      badge.className = 'satc-save';
      badge.textContent = `SPAR ${pct}%`;
      info.appendChild(badge);
    }

    /* ---------- Synlighed ---------- */

    requestUpdate() {
      if (document.visibilityState === 'hidden') {
        this.update();
        return;
      }
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    /**
     * @returns {boolean} Er en drawer, modal eller overlay åben?
     */
    static isBlocked() {
      return !!document.querySelector('.js-overlay.is-visible')
        || !!document.querySelector('cart-drawer[aria-hidden="false"]')
        || !!document.querySelector('quick-add-drawer[aria-hidden="false"]');
    }

    update() {
      const scrolledPast = this.anchor.getBoundingClientRect().bottom < 0;
      const nearFooter = window.scrollY + window.innerHeight + 120 > document.documentElement.scrollHeight;
      this.setOut(!(scrolledPast && !nearFooter && !StickyAtcPanel.isBlocked()));
      this.syncButtonLabel();
    }

    /**
     * @param {boolean} out - true = skjul baren.
     */
    setOut(out) {
      this.classList.toggle('sticky-atc-panel--out', out);
      // Temaets 'invisible' skal væk, ellers er baren skjult uanset alt andet.
      this.classList.toggle('invisible', out);
      document.body.classList.toggle('lh-satc-on', !out);

      if (!out) {
        const height = Math.round(this.getBoundingClientRect().height);
        if (height > 0 && height !== this.lastHeight) {
          this.lastHeight = height;
          document.documentElement.style.setProperty('--lh-satc-h', `${height}px`);
        }
      }
      this.liftWidgets(!out);
    }

    /**
     * Løfter chat/Hello Retail/PushOwl op over baren. Kun mobil — på desktop
     * er baren centreret og rammer dem ikke.
     *
     * Bemærk: der caches på BÅDE til/fra OG barens højde. Før cachede den kun
     * til/fra, så første løft skete med en for lav højde (før leveringsstriben
     * var målt med) og blev aldrig regnet om — chatten lå derfor oven på baren.
     * @param {boolean} on - true = løft, false = sæt tilbage.
     */
    liftWidgets(on) {
      const lift = on && window.matchMedia('(max-width: 768.98px)').matches;
      const height = this.lastHeight || 76;
      const key = lift ? height : 0;
      if (key === this.liftKey) return;
      this.liftKey = key;

      [['.wilmo-widget-launcher', 16], ['.pushowl-simple-toast', 12], ['#addwishPageAdd', 60]]
        .forEach((w) => {
          const el = document.querySelector(w[0]);
          if (!el) return;
          if (lift) el.style.setProperty('bottom', `${height + w[1]}px`, 'important');
          else el.style.removeProperty('bottom');
        });
    }

    /* ---------- Knap ---------- */

    /**
     * @returns {boolean} Kan varen lægges direkte i kurven?
     */
    canAddDirectly() {
      const btn = this.mainAddButton;
      if (!btn) return false;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return false;
      return btn.dataset.preselectionDisabled !== 'true';
    }

    syncButtonLabel() {
      if (!this.panelButton) return;
      const addable = this.canAddDirectly();
      if (addable === this.lastAddable) return;
      this.lastAddable = addable;

      if (this.detailsLabel === undefined) {
        this.detailsLabel = (this.panelButton.textContent || '').trim() || 'Se detaljer';
      }

      if (addable) {
        const label = (this.mainAddButton.dataset.addToCartText || '').trim() || 'Læg i kurv';
        this.panelButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/><path d="M2 3h2.5l2.2 11.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.6L21 7H5"/></svg>';
        this.panelButton.appendChild(document.createTextNode(label));
        this.panelButton.classList.add('is-add-to-cart');
      } else {
        this.panelButton.textContent = this.detailsLabel;
        this.panelButton.classList.remove('is-add-to-cart');
      }
    }

    /**
     * Kan varen købes direkte, kører vi hovedformularens "Tilføj til kurv", så
     * kurv-drawer og alt andet opfører sig normalt. Ellers lader vi
     * #product-info-linket virke.
     * @param {object} evt - Event object.
     */
    handleButtonClick(evt) {
      if (!this.canAddDirectly()) return;
      evt.preventDefault();
      this.mainAddButton.click();
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
